import {
	data,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import appleTouchIconAssetUrl from '#app/assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from '#app/assets/favicons/favicon.svg'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { MainFooter } from '#app/components/main-footer.tsx'
import { MainHeader } from '#app/components/main-header.tsx'
import { MainSidebar } from '#app/components/main-sidebar.tsx'
import { MobileHeader } from '#app/components/mobile-header.tsx'
import { EpicProgress } from '#app/components/progress-bar.tsx'
import { useToast } from '#app/components/toaster.tsx'
import { href as iconsHref } from '#app/components/ui/icon.tsx'
import { SidebarProvider } from '#app/components/ui/sidebar.tsx'
import { EpicToaster } from '#app/components/ui/sonner.tsx'
import { useIsMobile } from '#app/hooks/use-mobile.tsx'
import {
	useOptionalTheme,
	useTheme,
} from '#app/routes/_app+/resources+/theme-switch.tsx'
import tailwindStyleSheetUrl from '#app/styles/tailwind.css?url'
import { getUserId, logout } from '#app/utils/auth.server.ts'
import { ClientHintCheck, getHints } from '#app/utils/client-hints.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { getEnv } from '#app/utils/env.server.ts'
import { pipeHeaders } from '#app/utils/headers.server.ts'
import { honeypot } from '#app/utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl } from '#app/utils/misc.tsx'
import { useNonce } from '#app/utils/nonce-provider.ts'
import { type Theme, getTheme } from '#app/utils/theme.server.ts'
import { makeTimings, time } from '#app/utils/timing.server.ts'
import { getToast } from '#app/utils/toast.server.ts'
import { type Route } from './+types/root.tsx'

export const links: Route.LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ data }) => {
	return [
		{ title: data ? 'Clearwater Farms 1' : 'Error | Clearwater Farms 1' },
		{
			name: 'description',
			content: `Irrigation Management - Clearwater Farms Unit 1`,
		},
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUnique({
						select: {
							id: true,
							member: true,
							username: true,
							display: true,
							image: { select: { id: true } },
							ports: {
								select: {
									ditch: true,
									position: true,
									entry: true,
									section: true,
								},
							},
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()

	const schedules = await time(
		() =>
			prisma.activeSchedules.findFirst({
				select: {
					signupSchedule: {
						select: { date: true, deadline: true, state: true },
					},
					currentSchedule: { select: { date: true } },
					previousSchedule: { select: { date: true } },
				},
			}),
		{
			timings,
			type: 'find open schedule',
			desc: 'find open schedule in root',
		},
	)

	return data(
		{
			user,
			schedules: {
				previous: schedules?.previousSchedule,
				current: schedules?.currentSchedule,
				signup: schedules?.signupSchedule,
			},
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string | undefined>
}) {
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
	return (
		<html lang="en" className={`${theme} h-full overflow-x-hidden`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	const theme = useOptionalTheme()
	return (
		<Document nonce={nonce} theme={theme} env={data?.ENV}>
			{children}
		</Document>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	useToast(data.toast)

	const isMobile = useIsMobile()

	if (isMobile) {
		return (
			<SidebarProvider>
				<MainSidebar />
				<div className="flex min-h-screen flex-col">
					<MobileHeader />
					<main className="w-full">
						<Outlet />
					</main>
					<footer className="bg-white dark:bg-gray-900">
						<MainFooter />
					</footer>
				</div>
			</SidebarProvider>
		)
	}

	return (
		<div className="flex min-h-screen flex-col">
			<MainHeader />
			<main className="w-full">
				<Outlet />
			</main>
			<footer className="bg-white dark:bg-gray-900">
				<MainFooter />
			</footer>
		</div>
	)
}

function AppWithProviders() {
	const theme = useTheme()
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
			<EpicToaster closeButton position="top-center" theme={theme} />
			<EpicProgress />
		</HoneypotProvider>
	)
}

export default AppWithProviders

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
